import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import DomainItem from '../../components/DomainItem';
import DomainForm from '../../components/DomainForm';
import ConfirmationModal from '../../components/ConfirmationModal';
import { Plus } from 'lucide-react';
import api from '../../services/api';
import { useInterfaceStore } from '../../stores/useInterfaceStore';

export interface Domain {
  id: number;
  nome: string;
  descricao?: string;
  parent_id: number | null;
  framework_id: number;
  empresa_id: number;
  indice: string;
  subdomains: Domain[];
}

interface FlatDomain {
  id: number;
  nome: string;
  descricao?: string;
  parent_id: number | null;
  framework_id: number;
  empresa_id: number;
  indice: string;
}

interface IFramework {
  id: number;
  nome: string;
}

const ControlDomains: React.FC = () => {
  const { empresaId, tipoEmpresa } = useInterfaceStore();

  const tipoPessoa: string = (() => {
    try { return JSON.parse(localStorage.getItem('usuarioData') || '{}').tipo_pessoa || ''; }
    catch { return ''; }
  })();
  const canWrite = tipoEmpresa === '4Comply' || ['Administrador', 'DPO'].includes(tipoPessoa);

  const [domains, setDomains] = useState<Domain[]>([]);
  const [frameworks, setFrameworks] = useState<IFramework[]>([]);
  const [frameworkId, setFrameworkId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState<Domain | null>(null);
  const [newSubdomainParentId, setNewSubdomainParentId] = useState<number | null>(null);
  const [deletingDomainId, setDeletingDomainId] = useState<number | null>(null);

  // Carrega frameworks da empresa logada
  useEffect(() => {
    api.getFrameworks().then(res => {
      if (res.success && Array.isArray(res.data)) {
        setFrameworks(res.data as IFramework[]);
      }
    });
  }, []);

  const buildTree = (items: FlatDomain[]): Domain[] => {
    const tree: Domain[] = [];
    const lookup: { [id: number]: Domain } = {};
    items.forEach(item => { lookup[item.id] = { ...item, subdomains: [] }; });
    items.forEach(item => {
      if (item.parent_id && lookup[item.parent_id]) {
        lookup[item.parent_id].subdomains.push(lookup[item.id]);
      } else {
        tree.push(lookup[item.id]);
      }
    });
    Object.values(lookup).forEach(d => {
      d.subdomains.sort((a, b) => a.indice.localeCompare(b.indice, undefined, { numeric: true }));
    });
    tree.sort((a, b) => a.indice.localeCompare(b.indice, undefined, { numeric: true }));
    return tree;
  };

  const fetchDomains = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    setError(null);
    const result = await api.getDomains(empresaId);
    if (result.success && result.data) {
      const flat = result.data as FlatDomain[];
      const filtered = frameworkId
        ? flat.filter(d => d.framework_id === frameworkId)
        : flat;
      setDomains(buildTree(filtered));
    } else {
      setError('Erro ao buscar domínios.');
    }
    setLoading(false);
  }, [empresaId, frameworkId]);

  useEffect(() => { fetchDomains(); }, [fetchDomains]);

  // Índice sugerido para novo domínio ou subdomínio
  const getSuggestedIndex = (parentId: number | null): string => {
    if (parentId === null) {
      const mains = domains.filter(d => d.parent_id === null);
      const last = mains.reduce((max, d) => {
        const n = parseInt(d.indice, 10);
        return isNaN(n) ? max : Math.max(max, n);
      }, 0);
      return (last + 1).toString();
    }
    // Busca recursivamente nos subdomínios
    const findInTree = (list: Domain[], id: number): Domain | null => {
      for (const d of list) {
        if (d.id === id) return d;
        const found = findInTree(d.subdomains, id);
        if (found) return found;
      }
      return null;
    };
    const parent = findInTree(domains, parentId);
    if (!parent) return `${parentId}.1`;
    const last = parent.subdomains.reduce((max, s) => {
      const parts = s.indice.split('.');
      const n = parseInt(parts[parts.length - 1], 10);
      return isNaN(n) ? max : Math.max(max, n);
    }, 0);
    return `${parent.indice}.${last + 1}`;
  };

  const handleOpenFormForNewDomain = () => {
    if (!frameworkId) { setError('Selecione um Framework antes de adicionar um domínio.'); return; }
    setEditingDomain(null);
    setNewSubdomainParentId(null);
    setIsFormOpen(true);
  };

  const handleOpenFormForNewSubdomain = (parentId: number) => {
    setEditingDomain(null);
    setNewSubdomainParentId(parentId);
    setIsFormOpen(true);
  };

  const handleOpenFormForEdit = (domain: Domain) => {
    setEditingDomain(domain);
    setNewSubdomainParentId(null);
    setIsFormOpen(true);
  };

  const handleOpenConfirmDelete = (domainId: number) => {
    setDeletingDomainId(domainId);
    setIsConfirmOpen(true);
  };

  const handleCloseModals = () => {
    setIsFormOpen(false);
    setIsConfirmOpen(false);
    setEditingDomain(null);
    setNewSubdomainParentId(null);
    setDeletingDomainId(null);
  };

  const handleSave = async (data: Partial<Domain>) => {
    const isEditing = !!data.id;
    const fwId = data.framework_id || frameworkId;
    if (!fwId) { setError('Framework não selecionado.'); return; }

    const payload = { ...data, empresa_id: empresaId, framework_id: fwId };
    const result = isEditing
      ? await api.updateDomain(data.id!, payload)
      : await api.createDomain(payload);

    if (result.success) {
      setSuccess(`Domínio ${isEditing ? 'atualizado' : 'criado'} com sucesso!`);
      handleCloseModals();
      fetchDomains();
      setTimeout(() => setSuccess(null), 3000);
    } else {
      const msg = (result.data as any)?.error || result.error || 'Erro desconhecido';
      setError(`Erro: ${msg}`);
    }
  };

  const handleDelete = async () => {
    if (!deletingDomainId) return;
    const result = await api.deleteDomain(deletingDomainId);
    if (result.success) {
      setSuccess('Domínio excluído com sucesso!');
      handleCloseModals();
      fetchDomains();
      setTimeout(() => setSuccess(null), 3000);
    } else {
      const msg = (result.data as any)?.error || result.error || 'Erro desconhecido';
      setError(`Erro ao excluir: ${msg}`);
    }
  };

  const selectedFrameworkNome = frameworks.find(f => f.id === frameworkId)?.nome;

  return (
    <Container fluid className="py-4">
      {/* Cabeçalho */}
      <Row className="mb-3 align-items-center">
        <Col>
          <h1 className="mb-0">Domínios de Controle</h1>
          <p className="text-muted mb-0">Gerencie a hierarquia de domínios por framework.</p>
        </Col>
        {canWrite && (
          <Col xs="auto">
            <Button
              onClick={handleOpenFormForNewDomain}
              disabled={!frameworkId}
              title={!frameworkId ? 'Selecione um framework primeiro' : undefined}
            >
              <Plus size={16} className="me-1" />
              Novo Domínio
            </Button>
          </Col>
        )}
      </Row>

      {/* Alertas */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>
      )}
      {!empresaId && (
        <Alert variant="warning">Nenhuma empresa selecionada. Selecione uma empresa para continuar.</Alert>
      )}
      {!canWrite && (
        <Alert variant="info">Você tem acesso somente de <strong>leitura</strong> neste módulo.</Alert>
      )}

      {/* Seletor de Framework */}
      <Row className="mb-4">
        <Col md={5}>
          <Form.Group controlId="frameworkSelect">
            <Form.Label><strong>Framework</strong></Form.Label>
            <Form.Select
              value={frameworkId ?? ''}
              onChange={e => setFrameworkId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Todos os frameworks</option>
              {frameworks.map(f => (
                <option key={f.id} value={f.id}>{f.nome}</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              {frameworkId
                ? `Exibindo domínios de: ${selectedFrameworkNome}`
                : 'Exibindo domínios de todos os frameworks'}
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      {/* Lista de domínios */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : domains.length === 0 ? (
        <Alert variant="info">
          {frameworkId
            ? `Nenhum domínio cadastrado para o framework "${selectedFrameworkNome}". Clique em "Novo Domínio" para começar.`
            : 'Nenhum domínio cadastrado. Selecione um framework e clique em "Novo Domínio".'}
        </Alert>
      ) : (
        <>
          <div className="mb-2 text-muted small">
            <Badge bg="light" text="dark" className="me-2">{domains.length} domínio(s) principal(is)</Badge>
            <Badge bg="light" text="dark">{domains.reduce((acc, d) => acc + d.subdomains.length, 0)} subdomínio(s)</Badge>
          </div>
          <ul className="list-unstyled">
            {domains.map(domain => (
              <li key={domain.id} className="mb-3">
                <DomainItem
                  domain={domain}
                  onAddSubdomain={canWrite ? handleOpenFormForNewSubdomain : () => {}}
                  onEdit={canWrite ? handleOpenFormForEdit : () => {}}
                  onDelete={canWrite ? handleOpenConfirmDelete : () => {}}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Modal de formulário */}
      {isFormOpen && (
        <DomainForm
          domain={editingDomain}
          parentId={newSubdomainParentId}
          empresaId={empresaId ?? 0}
          frameworkId={editingDomain?.framework_id ?? frameworkId ?? 0}
          onSave={handleSave}
          onCancel={handleCloseModals}
          suggestedIndex={
            editingDomain ? editingDomain.indice : getSuggestedIndex(newSubdomainParentId)
          }
        />
      )}

      {/* Modal de confirmação de exclusão */}
      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={handleCloseModals}
        onConfirm={handleDelete}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este domínio? Subdomínios associados também serão removidos. Esta ação não pode ser desfeita."
      />
    </Container>
  );
};

export default ControlDomains;
